/**
 * AddressList — the residential-address sub-section of the Contact
 * Details tab. Owns the array, fans out per-row mutations to
 * `AddressRow`, and enforces the single-default invariant.
 *
 * Flutter parity: `residential_address_form.dart` (the BLoC-driven
 * version). Same array shape, same isDefault / isDeleted semantics.
 *
 * Default invariant:
 *   - Exactly one non-deleted row carries isDefault=true
 *   - Toggling a row to default clears default on the others
 *   - Deleting the current default promotes the first non-deleted
 *     remaining row to default (Flutter `:166-174`)
 */

import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { RecordListAdd, RecordListEmpty } from '@/shared/ui/components/record-row-card';
import {
  defaultAddressRow,
  type AddressFormValues,
  type ContactDetailsFormValues,
} from './contact-details-schema';
import { AddressRow } from './address-row';

export interface AddressListProps {
  readOnly?: boolean;
}

export function AddressList({ readOnly }: AddressListProps) {
  // PROD-2743 · switch ON: the DS `add` glyph (one icon system, 16px inline); OFF = today's lucide.
  const { control, getValues } = useFormContext<ContactDetailsFormValues>();
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'addresses',
  });

  const liveRowCount = fields.filter(
    (_, i) => !(getValues(`addresses.${i}.isDeleted`) ?? false),
  ).length;

  const handleAdd = () => {
    if (readOnly) return;
    const next = defaultAddressRow();
    // First row is default by convention. Subsequent rows added by the
    // user start non-default — they can promote via the row toggle.
    if (liveRowCount === 0) next.isDefault = true;
    append(next);
  };

  const handleRowChange = (i: number, next: AddressFormValues) => {
    // If this row was just toggled to default, demote all others.
    const wasDefault = getValues(`addresses.${i}.isDefault`);
    if (next.isDefault && !wasDefault) {
      fields.forEach((_, idx) => {
        if (idx !== i) update(idx, { ...getValues(`addresses.${idx}`), isDefault: false });
      });
    }
    update(i, next);
  };

  const handleCancel = (i: number) => {
    const row = getValues(`addresses.${i}`);
    if (!row?.contactId) {
      // Never-saved row → splice out entirely.
      remove(i);
      return;
    }
    // Existing row — just exit edit mode. The form's dirty-state will
    // still be marked from any in-flight edits but next refetch will
    // restore. Flutter does the same: cancel on a saved row keeps the
    // row but clears isEdit.
    update(i, { ...row, isEdit: false });
  };

  const handleRemove = (i: number) => {
    if (readOnly) return;
    const row = getValues(`addresses.${i}`);
    if (!row) return;
    if (!row.contactId) {
      // Never-saved row — splice.
      remove(i);
      return;
    }
    // Soft-delete. If this was the default, promote the next live row.
    update(i, { ...row, isDeleted: true, isEdit: false });
    if (row.isDefault) {
      const candidates = fields
        .map((_, idx) => idx)
        .filter((idx) => {
          if (idx === i) return false;
          const candidate = getValues(`addresses.${idx}`);
          return candidate && !candidate.isDeleted;
        });
      if (candidates.length > 0) {
        const promoteIdx = candidates[0];
        update(promoteIdx, { ...getValues(`addresses.${promoteIdx}`), isDefault: true });
      }
    }
  };

  const visibleRows = fields
    .map((field, i) => ({ field, i }))
    .filter(({ i }) => !(getValues(`addresses.${i}.isDeleted`) ?? false));

  return (
    // The approved design's PaRecordList: the rows 12 apart, the empty line, the "Add …" row at the foot.
    <div data-slot="record-list">
      {visibleRows.length === 0 ? (
        <>
          <RecordListEmpty>No address on file.</RecordListEmpty>
          {!readOnly ? <RecordListAdd onClick={handleAdd}>Add address</RecordListAdd> : null}
        </>
      ) : (
        <>
          {visibleRows.map(({ field, i }) => (
            <Controller
              key={field.id}
              control={control}
              name={`addresses.${i}`}
              render={({ field: rowField }) => (
                <AddressRow
                  row={rowField.value}
                  index={i}
                  isOnly={visibleRows.length <= 1}
                  readOnly={readOnly}
                  onChange={(next) => handleRowChange(i, next)}
                  onCancel={() => handleCancel(i)}
                  onRemove={() => handleRemove(i)}
                />
              )}
            />
          ))}
          {!readOnly ? (
            <RecordListAdd onClick={handleAdd}>Add address</RecordListAdd>
          ) : null}
        </>
      )}
    </div>
  );
}
